import { useCallback, useRef, useState } from "react";
import { AttachmentMeta } from "@/indexedDB/types";
import { processFileAttachment } from "@/middleware/attachments";
import { saveFile } from "@/lib/saveFile";

export type FileDownloadStatus = "idle" | "downloading" | "error";

type UseFileDownloadOptions = {
  chatId: string;
  messageId: string;
  timestamp: string;
  attachment?: AttachmentMeta;
};

/**
 * Download-on-click for a document attachment: fetch + decrypt the full file
 * (reusing the same `processFileAttachment` path as the details drawer), then
 * save it to disk. Exposes a small status machine for the file card to reflect
 * (idle → downloading → idle | error). Re-clicking retries.
 *
 * There is deliberately no success state. The platform already confirms a save
 * (the browser's download shelf, or the iOS share sheet), and the file leaves
 * the app's control the moment it's written — so the card returns to
 * advertising the action, which stays repeatable. "error" persists because it
 * IS actionable.
 *
 * Byte progress is NOT returned here: `processFileAttachment` publishes it to
 * the id-keyed `useDownloadProgressState` store instead, because the download is
 * single-flight deduped and a per-caller callback would be dropped for every
 * joiner but the first. Consumers subscribe by attachment id.
 */
const useFileDownload = ({
  chatId,
  messageId,
  timestamp,
  attachment,
}: UseFileDownloadOptions) => {
  const [status, setStatus] = useState<FileDownloadStatus>("idle");
  const inFlight = useRef(false);

  const download = useCallback(async () => {
    if (!attachment || inFlight.current) {
      return;
    }
    inFlight.current = true;
    setStatus("downloading");
    try {
      const blob = await processFileAttachment({
        chatId,
        messageId,
        timestamp,
        attachmentMeta: attachment,
      });
      // `!blob` alone is not enough: an empty Blob is truthy, so a 0-byte
      // decrypt result would sail through and be saved to disk as an empty
      // file reported as a success. Guarded at the source too (see
      // decryptMessageAttachment) — this is the belt-and-braces half.
      if (!blob || blob.size === 0) {
        setStatus("error");
        return;
      }
      await saveFile(blob, attachment.filename);
      // Straight back to idle — see the note above on why there is no success
      // state. "error" deliberately stays put so the failure remains visible.
      setStatus("idle");
    } catch {
      setStatus("error");
    } finally {
      inFlight.current = false;
    }
  }, [chatId, messageId, timestamp, attachment]);

  return { status, download };
};

export default useFileDownload;
